'use client';

import React, { Fragment } from 'react';
import { useSearchParams } from 'next/navigation';

import { useAuthContext } from '../../../data/context/authContext';
import useStartConsultationCtaText from './hooks/useStartConsultationCtaText';
import useStartConsultation, { getConsultationSessionData } from '../hooks/useStartConsultation';

import Button from '../../_ui/_blocks/Buttons/Button/Button';
import ConsultationAssessmentOdonlyPoints from './ConsultationAssessmentOdonlyPoints';
import ConsultationAssessmentPoints from './ConsultationAssessmentPoints';
import Card from '../../_ui/_blocks/Card/Card';
import Typography from '../../_ui/_blocks/Typography/Typography';

import styles from './ConsultationAssessmentCard.module.css';

interface Props {
    conditionId: number;
    conditionSlug: string;
    conditionName: string;
    hasRecommendedTreatments?: boolean;
    hasOdonlyRecommendedTreatment?: boolean;
    children: React.ReactNode;
}

// TODO: Flip buttons around based on if they have logged in before or not.
// TODO: Clicking one of the start consultation buttons sets both to loading, may or may not be a problem.

const ConsultationAssessmentCard = ({
    conditionSlug, conditionId, conditionName, hasRecommendedTreatments, hasOdonlyRecommendedTreatment, children
}: Props) => {
    const { isLoggedIn, authedBefore } = useAuthContext();
    const startConsultation = useStartConsultation();

    const { primaryCta, secondaryCta } = useStartConsultationCtaText();

    const searchParams = useSearchParams();

    // If the customer has come to this page from a treatment page, we want to carry over any existing
    // session data from the treatment selector for analytics reasons.
    const getExistingConsultationSessionData = () => {
        // If there is no variant in the URL, the user hasn't come from the treatment page.
        if (!searchParams?.has('variant')) {
            return {};
        }

        const existingConsultationSessionData = getConsultationSessionData(conditionId);

        // Ensures treatmentId and quantity are not sent as
        // - "treatmentId" will prevent the treatment recommendation panel from being sent by API.
        // - "quantity" causes an API error for the panels request if it's the only value sent.
        if (existingConsultationSessionData && 'treatmentId' in existingConsultationSessionData && existingConsultationSessionData.treatmentId) {
            // Make sure we don't send the treatmentId
            // @ts-expect-error
            delete existingConsultationSessionData.treatmentId
        }
        if (existingConsultationSessionData && 'quantity' in existingConsultationSessionData && existingConsultationSessionData.quantity) {
            // Make sure we don't send the quantity
            // @ts-expect-error
            delete existingConsultationSessionData.quantity
        }

        const variantIdFromSession = existingConsultationSessionData && ('selectedVariant' in existingConsultationSessionData)
            ? existingConsultationSessionData?.selectedVariant?.id
            : null;

        // If the variant in the URL matches the variant in the session, we want to return the session data.
        return Number(searchParams.get('variant')) === variantIdFromSession ? existingConsultationSessionData : {};
    }

    const handleStartConsultation = (introRequired: boolean) => {
        startConsultation(conditionSlug, {
            ...getExistingConsultationSessionData(),
            conditionId,
            conditionName,
            introRequired,
        });
    }

    return (
        <div className={styles.assessment}>
            <div>
                <Typography as="h2" typeset="title" size="700" lineHeight="900" className={styles.sectionTitle}>
                    {hasOdonlyRecommendedTreatment ? (
                        <Fragment>
                            Start your <span>assessment for {conditionName}</span>
                        </Fragment>
                    ) : (
                        <Fragment>
                            View recommended <span>products for {conditionName}</span>
                        </Fragment>
                    )}
                </Typography>
                <ul className="space-y-100 mt-rhythm-small hidden md:block">
                    {hasOdonlyRecommendedTreatment ? <ConsultationAssessmentOdonlyPoints /> : <ConsultationAssessmentPoints />}
                </ul>
            </div>
            <Card variant="bordered" className={styles.card} borderColor="accent" disableBoxShadow padding="none">
                <Typography as="h3" typeset="subtitle" size="500" lineHeight="500" className={styles.cardHeader}>
                    Don't wait to get the medical help you need.
                </Typography>
                <Typography as="p" typeset="emphasis" size="100" className="mt-050">
                    {children}
                </Typography>
                {hasRecommendedTreatments ? (
                    <Fragment>
                        <Button
                            type="button"
                            onClick={() => handleStartConsultation(primaryCta.introRequired)}
                            size="large"
                            title={primaryCta.text}
                            variant="primary"
                            className="w-full mt-200 !px-100"
                        >
                            {primaryCta.text}
                        </Button>
                        {!isLoggedIn ? (
                            <div className="mt-100 type-090">
                                <div className="text-center text-content-quiet">
                                    {/* If they've authed before, we need to display the opposite question */}
                                    {authedBefore ? 'First time visiting us?' : 'Ordered with us before?'}
                                </div>
                                <button
                                    onClick={() => handleStartConsultation(secondaryCta.introRequired)}
                                    type="button"
                                    className="type-link w-full"
                                >
                                    {secondaryCta.text}
                                </button>
                            </div>
                        ) : null}
                    </Fragment>
                ) : null}
            </Card>
            <ul className="space-y-100 md:hidden">
                {hasOdonlyRecommendedTreatment ? <ConsultationAssessmentOdonlyPoints /> : <ConsultationAssessmentPoints />}
            </ul>
        </div>
    );
};

export default ConsultationAssessmentCard;
